import { CheckCircle2, ChevronDown, Plus, X } from "lucide-react";

import { useEffect, useMemo, useState } from "react";

import { Document, Thumbnail } from "react-pdf";

import "react-pdf/dist/esm/Page/TextLayer.css";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/spinner";
import { TypographyLarge, TypographySmall } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

import type { InvoiceData } from "@/apis/gql/types";
import { Count } from "@/components/shared/data-table/total-count";
import { useInvoicePdfCache } from "@/providers/invoice-pdf-cache";
import { useGetInvoicesForPageEditor } from "@/queries/invoices/edit-pages";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { useTranslation } from "react-i18next";

const AddPagesViewerItem = ({
  invoice,
  supplierName,
  matches,
  selected,
  isDigitalInvoice = false,
  onSelect,
  onAdd,
}: {
  invoice: InvoiceData;
  supplierName: string;
  matches: {
    supplier: boolean;
    invoiceNo: boolean;
  };
  selected: boolean;
  isDigitalInvoice?: boolean;
  onSelect: (invoiceId: string, pdfUrl: string) => void;
  onAdd: (
    invoice: InvoiceData,
    supplierName: string,
    pdfUrl: string,
    document: PDFDocumentProxy,
  ) => void;
}) => {
  const { t } = useTranslation();
  const { fetchPdfUrl } = useInvoicePdfCache();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);

  useEffect(() => {
    fetchPdfUrl(invoice.invoiceId).then(setPdfUrl);
  }, [invoice.invoiceId]);

  const matchesString = useMemo(() => {
    const s = [];
    if (matches.supplier) {
      s.push("Kreditor");
    }
    if (matches.invoiceNo) {
      s.push("Re.Nr.");
    }
    return s.join(" & ");
  }, [matches]);

  return (
    <div className="relative">
      <div
        onClick={() => {
          if (pdfUrl) {
            onSelect(invoice.invoiceId, pdfUrl);
          }
        }}
        className={cn(
          "mb-4 p-2 border-2 border-transparent rounded-md group flex flex-col items-center cursor-pointer",
          selected
            ? "border-blue-400 bg-blue-50"
            : "hover:border-gray-300 hover:border-dotted hover:bg-gray-50",
        )}
      >
        <div className="w-32 flex flex-col gap-2">
          <div className="relative w-full h-48 flex items-center justify-center overflow-hidden border border-gray-200 bg-white">
            {pdfUrl && (
              <div
                className={cn(
                  "w-full h-full",
                  isDigitalInvoice && "opacity-50",
                )}
              >
                <Document
                  file={pdfUrl}
                  onLoadSuccess={(d) => setDocument(d as any)}
                  loading={null}
                >
                  <Thumbnail pageIndex={0} width={128} height={192} />
                </Document>
              </div>
            )}
            {pdfUrl && document && (
              <div className="absolute top-0 right-0 bg-white rounded-md p-1">
                <div className="flex items-center gap-2">
                  {isDigitalInvoice && (
                    <Badge variant="default" className="text-[10px]">
                      {t("e_invoice")}
                    </Badge>
                  )}
                  <Count count={document?.numPages ?? 0} />
                </div>
              </div>
            )}
            {pdfUrl && document && !isDigitalInvoice && (
              <div
                className="absolute flex justify-center group-hover:opacity-100 bottom-2"
                title={invoice.fileName}
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="!px-1.5 !py-2 text-sm h-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (document) {
                      onAdd(invoice, supplierName, pdfUrl, document);
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                  <TypographySmall>Hinzufügen</TypographySmall>
                </Button>
              </div>
            )}
          </div>
          <div
            className={cn(
              "w-full text-xs text-muted-foreground text-ellipsis overflow-hidden whitespace-nowrap",
              isDigitalInvoice && "opacity-50",
            )}
          >
            <TypographySmall>{invoice.fileName}</TypographySmall>
          </div>
        </div>

        {(matches.supplier || matches.invoiceNo) && (
          <div
            className={cn(
              "flex justify-center items-center mt-2",
              isDigitalInvoice && "opacity-50",
            )}
          >
            <Badge
              variant={
                matches.supplier && matches.invoiceNo ? "success" : "warning"
              }
              className="px-2 py-1"
            >
              <CheckCircle2 className="w-4 h-4 mr-2 -ml-1" />
              <TypographySmall>{matchesString}</TypographySmall>
            </Badge>
          </div>
        )}
        {isDigitalInvoice && (
          <div className="flex justify-center items-center mt-2 text-red-500/80 text-[12px] opacity-0 group-hover:opacity-100 text-center">
            Heften nicht möglich (E-Rechnung)
          </div>
        )}
      </div>
    </div>
  );
};

export const AddPagesViewer = ({
  sourceInvoiceId,
  excludeInvoiceIds,
  onClose,
  onSelectInvoice,
  selectedInvoiceId,
  onAddInvoice,
}: {
  sourceInvoiceId?: string;
  excludeInvoiceIds: string[];
  selectedInvoiceId: string | null;
  onClose: () => void;
  onSelectInvoice: (invoiceId: string, pdfUrl: string) => void;
  onAddInvoice: (
    invoice: InvoiceData,
    supplierName: string,
    pdfUrl: string,
  ) => void;
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const { data, loading } = useGetInvoicesForPageEditor({
    sourceInvoiceId,
    excludeInvoiceIds,
    searchQuery,
  });

  const [visibleCount, setVisibleCount] = useState(10);

  const invoicesCount = data?.invoicesForPageEditor?.invoices?.length;

  const handleSelectInvoice = (invoiceId: string, pdfUrl: string) => {
    onSelectInvoice(invoiceId, pdfUrl);
  };

  return (
    <div className="basis-96 h-full overflow-hidden border border-gray-200 rounded-l-md p-2 flex flex-col gap-2 overflow-y-auto">
      <div className="flex items-center justify-between px-2">
        <TypographyLarge>{t("pdf_add_pages")}</TypographyLarge>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            onClose();
          }}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="mb-2 px-2">
        <Input
          placeholder="Suchen nach Rechnungsnr, Kreditor, Dateiname"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="text-xs text-muted-foreground mt-2">
          {t("pdf_add_pages_instructions")}
        </div>
      </div>
      {loading && <LoadingSpinner />}
      {data && invoicesCount !== undefined && (
        <>
          {invoicesCount === 0 ? (
            <div>
              <TypographySmall>Keine Rechnungen gefunden</TypographySmall>
            </div>
          ) : (
            <div className="flex flex-col gap-0.5 overflow-y-auto">
              <div className="grid grid-cols-2 gap-0.5">
                {data.invoicesForPageEditor.invoices
                  .slice(0, visibleCount)
                  .map((invoice) => (
                    <AddPagesViewerItem
                      key={invoice.invoice.invoiceId}
                      invoice={invoice.invoice}
                      supplierName={invoice.invoice.supplierName ?? ""}
                      matches={invoice.matches}
                      selected={selectedInvoiceId === invoice.invoice.invoiceId}
                      isDigitalInvoice={invoice.invoice.isDigitalInvoice}
                      onSelect={handleSelectInvoice}
                      onAdd={onAddInvoice}
                    />
                  ))}
              </div>
              {invoicesCount > visibleCount && (
                <div className="flex justify-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setVisibleCount((c) => c + 10)}
                  >
                    <TypographySmall>Mehr anzeigen</TypographySmall>
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
